import { Assignment, Close, Save } from '@mui/icons-material';
import { Box, Button, Fade, FormControl, Grid, TextField, Typography } from '@mui/material';
import { deepEqual } from 'fast-equals';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import * as Yup from 'yup';
// Components
import { Card, Loader, Modal } from '../../../../components';
import { updateVendorContact, addVendorContact } from '../../../../fetch';
import { formatInputPhoneNumber } from '../../../../helpers';
import { ISageVendorContact } from '../../../../models';

interface IVendorContactModal {
  open: boolean;
  onClose: () => void;
  currentVendorContact?: ISageVendorContact;
  fetchSageVendorContacts: () => {};
  currentVendorId: string | number;
}

interface IVendorContactModalValues {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phoneNumber: string;
  cellPhoneNumber: string;
}

const VendorContactSchema = Yup.object().shape({
  firstName: Yup.string().required('Required').max(255, 'Max 255 characters'),
  lastName: Yup.string().required('Required').max(255, 'Max 255 characters'),
  email: Yup.string().email().required('Required'),
  title: Yup.string(),
  phoneNumber: Yup.string().max(255, 'Max 255 characters'),
  cellPhoneNumber: Yup.string().max(255, 'Max 255 characters')
});

export const VendorContactModal: FC<IVendorContactModal> = ({
  currentVendorId,
  open,
  onClose,
  currentVendorContact,
  fetchSageVendorContacts
}) => {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          // split the `name` property on the space to get first and last name
          firstName: currentVendorContact?.name?.split(' ')?.[0] ?? '',
          lastName: currentVendorContact?.name?.split(' ')?.[1] ?? '',
          email: currentVendorContact?.email ?? '',
          title: currentVendorContact?.jobTitle ?? '',
          phoneNumber:
            currentVendorContact && currentVendorContact.phoneNumber
              ? formatInputPhoneNumber(currentVendorContact.phoneNumber)
              : '',
          cellPhoneNumber:
            currentVendorContact && currentVendorContact.cellPhoneNumber
              ? formatInputPhoneNumber(currentVendorContact.cellPhoneNumber)
              : ''
        }}
        validationSchema={VendorContactSchema}
        onSubmit={async (values, actions) => {
          if (currentVendorContact) {
            try {
              await updateVendorContact(currentVendorContact.sageContactId, values);
              enqueueSnackbar('Contact Updated!', {
                variant: 'success'
              });
              onClose();
              fetchSageVendorContacts();
              actions.resetForm();
            } catch (error) {
              console.log(error);
              if (error && error.response && error.response.data && error.response.data.Detail) {
                enqueueSnackbar(error.response.data.Detail, {
                  variant: 'error'
                });
                // Fallback case if no error is provided.
              } else {
                enqueueSnackbar('Contact was not updated', {
                  variant: 'error'
                });
              }
            }
          } else {
            try {
              await addVendorContact(currentVendorId, values);
              enqueueSnackbar('Contact Created!', {
                variant: 'success'
              });
              onClose();
              fetchSageVendorContacts();
              actions.resetForm();
            } catch (error) {
              if (error && error.response && error.response.data && error.response.data.Detail) {
                enqueueSnackbar(error.response.data.Detail, {
                  variant: 'error'
                });
                // Fallback case if no error is provided.
              } else {
                enqueueSnackbar('Contact was not created', {
                  variant: 'error'
                });
              }
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                }
              }}
              maxWidth="md"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box marginBottom="1rem">
                    <Typography variant="h5">
                      {currentVendorContact ? 'Edit Contact' : 'Add Contact'}
                    </Typography>
                  </Box>
                  <Card title="Contact Info" icon={<Assignment />}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          variant="outlined"
                          autoComplete="nope"
                          label="First Name"
                          name="firstName"
                          value={values.firstName}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('firstName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          variant="outlined"
                          autoComplete="nope"
                          label="Last Name"
                          name="lastName"
                          value={values.lastName}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('lastName', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <Field name="email">
                            {({ field, form }: FieldProps<IVendorContactModalValues>) => (
                              <TextField
                                {...field}
                                error={
                                  form.touched.email && form.errors && form.errors.email
                                    ? true
                                    : false
                                }
                                fullWidth={true}
                                helperText={form.touched.email && form.errors && form.errors.email}
                                id="email"
                                label="Email"
                                size="small"
                                required
                                type="text"
                                autoComplete="off"
                                value={values.email}
                              />
                            )}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Title"
                          name="title"
                          value={values.title}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => setFieldValue('title', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Phone Number"
                          name="phone"
                          value={values.phoneNumber}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => {
                            setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
                          }}
                          error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                          helperText={touched.phoneNumber && errors.phoneNumber}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          autoComplete="nope"
                          label="Cell Phone Number"
                          name="phone"
                          value={values.cellPhoneNumber}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => {
                            setFieldValue(
                              'cellPhoneNumber',
                              formatInputPhoneNumber(e.target.value)
                            );
                          }}
                          error={Boolean(touched.cellPhoneNumber && errors.cellPhoneNumber)}
                          helperText={touched.cellPhoneNumber && errors.cellPhoneNumber}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
